import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface DatasetRow {
  readonly datasetId: string;
  readonly datasetCode: string;
  readonly datasetName: string;
  readonly sourceDomain: string;
  readonly dataStructure: string;
  readonly ingestionMode: string;
  readonly zone: string;
  readonly loadStatus: string;
  readonly qualityScore: string | null;
  readonly classification: string;
  readonly rowCount: string;
  readonly status: string;
}

const SOURCE_DOMAINS = ['CAMPAIGN', 'BUDGET', 'VENDOR', 'CONTRACT', 'WORK_ORDER', 'ASSET', 'APPROVAL', 'DOCUMENT', 'USER', 'KNOWLEDGE', 'ANALYTICS', 'AUDIT_LOG', 'SYSTEM_EVENT', 'EXTERNAL'];
const DATA_STRUCTURES = ['STRUCTURED', 'SEMI_STRUCTURED', 'UNSTRUCTURED'];
const INGESTION_MODES = ['BATCH', 'NEAR_REAL_TIME', 'SCHEDULED', 'INCREMENTAL', 'SYNC', 'API', 'FILE'];
const ZONES = ['RAW', 'CURATED', 'ANALYTICAL'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const PAGE = 25;

/**
 * M41 — Dataset Catalog (§26 / §6 / §8 / §13). The registry surface over the
 * data lake's datasets (T535): filter/page the catalog, register a new governed
 * dataset by clean reference to its authoritative source (LAW 1 — never a copy),
 * and trigger a governed load / refresh (§7). Viewing needs DATASET_VIEW,
 * registration needs DATASET_MANAGE, loading needs DATASET_LOAD.
 */
export function DatasetCatalog(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DatasetRow[]>([]);
  const [total, setTotal] = useState(0);
  const [sourceDomain, setSourceDomain] = useState('');
  const [zone, setZone] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('CAMPAIGN');
  const [structure, setStructure] = useState('STRUCTURED');
  const [mode, setMode] = useState('BATCH');
  const [newZone, setNewZone] = useState('RAW');
  const [classification, setClassification] = useState('INTERNAL');
  const [sourceRef, setSourceRef] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (sourceDomain) params.set('sourceDomain', sourceDomain);
    if (zone) params.set('zone', zone);
    if (search) params.set('search', search);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: DatasetRow[]; total: number }>(`/v1/data-lake/datasets?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, sourceDomain, zone, search, page, reload]);

  const register = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m41.catalog.nameRequired')); return; }
    try {
      await apiRequest('/v1/data-lake/datasets', {
        method: 'POST',
        body: {
          datasetName: name.trim(),
          sourceDomain: domain,
          dataStructure: structure,
          ingestionMode: mode,
          zone: newZone,
          classification,
          sourceRef: sourceRef.trim() || null,
        },
        token,
      });
      setNotice(t('m41.catalog.registered'));
      setName('');
      setSourceRef('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.catalog.notAuthRegister'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const load = async (datasetId: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/data-lake/datasets/${datasetId}/load`, { method: 'POST', body: { refresh: true }, token });
      setNotice(t('m41.catalog.loadTriggered'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m41.catalog.notAuthLoad'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="dataset-catalog">
      <h1>{t('m41.catalog.title')}</h1>

      <section aria-label="dataset-register">
        <h2>{t('m41.catalog.register')}</h2>
        <form aria-label="register-form" onSubmit={(e) => void register(e)}>
          <label>{t('m41.common.name')}<input aria-label="field-dataset-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m41.catalog.sourceDomain')}
            <select aria-label="field-source-domain" value={domain} onChange={(e) => setDomain(e.target.value)}>
              {SOURCE_DOMAINS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.catalog.structure')}
            <select aria-label="field-data-structure" value={structure} onChange={(e) => setStructure(e.target.value)}>
              {DATA_STRUCTURES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.catalog.ingestionMode')}
            <select aria-label="field-ingestion-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
              {INGESTION_MODES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.common.zone')}
            <select aria-label="field-zone" value={newZone} onChange={(e) => setNewZone(e.target.value)}>
              {ZONES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.common.classification')}
            <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
              {CLASSIFICATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m41.common.sourceRef')}<input aria-label="field-source-ref" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-register">{t('m41.catalog.registerBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="dataset-action-error">{actionError}</p>}
        {notice && <p aria-label="dataset-notice">{notice}</p>}
      </section>

      <section aria-label="dataset-filters">
        <label>{t('m41.catalog.sourceDomain')}
          <select aria-label="filter-source-domain" value={sourceDomain} onChange={(e) => { setSourceDomain(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {SOURCE_DOMAINS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('m41.common.zone')}
          <select aria-label="filter-zone" value={zone} onChange={(e) => { setZone(e.target.value); setPage(0); }}>
            <option value="">{t('m41.common.all')}</option>
            {ZONES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('common.search')}<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="dataset-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="dataset-total">{t('m41.common.datasetsCount', { count: total })}</p>
          <DataTable
            ariaLabel="dataset-table"
            rows={rows}
            rowKey={(d) => d.datasetId}
            emptyMessage={t('m41.common.noDatasets')}
            columns={[
              { key: 'code', header: t('m41.common.code'), cellAriaLabel: (d) => `dataset-code-${d.datasetId}`, render: (d) => d.datasetCode },
              { key: 'name', header: t('m41.common.name'), render: (d) => d.datasetName },
              { key: 'domain', header: t('m41.common.domain'), render: (d) => d.sourceDomain },
              { key: 'zone', header: t('m41.common.zone'), render: (d) => d.zone },
              { key: 'load', header: t('m41.common.loadCol'), cellAriaLabel: (d) => `load-status-${d.datasetId}`, render: (d) => d.loadStatus },
              { key: 'quality', header: t('m41.common.quality'), render: (d) => decimal(d.qualityScore) },
              { key: 'classification', header: t('m41.common.classification'), render: (d) => d.classification },
              { key: 'rows', header: t('m41.catalog.rows'), render: (d) => d.rowCount },
              { key: 'actions', header: t('m41.common.actions'), render: (d) => (
                <Button variant="secondary" type="button" aria-label={`load-${d.datasetId}`} onClick={() => void load(d.datasetId)}>{t('m41.catalog.load')}</Button>
              ) },
            ]}
          />

          <nav aria-label="dataset-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m41.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m41.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m41.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
